// src/main.tsx
import React from "react";
import ReactDOM from "react-dom/client";
import "./aws-config";
import "./styles/tokens.css";
import "./styles/global.css";
import "./styles/tailwind.css";

import { BaseProvider, LightTheme, createTheme } from "baseui";
import { ToasterContainer } from "baseui/toast";
import { BrowserRouter } from "react-router-dom";
import { Client as Styletron } from "styletron-engine-atomic";
import { Provider as StyletronProvider } from "styletron-react";
import { LayoutPage } from "./pages/LayoutPage.tsx";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BusinessProvider } from "./context/BusinessContext";
import { useTenantBranding } from "./hooks/useTenantBranding";
import ErrorBoundary from "./components/ErrorBoundary";
import { initSentry } from "./sentry";
import { AdminLangProvider } from "./i18n/admin";

// SAM-226: initialize error reporting as early as possible so failures during
// the initial render are captured. No-op unless VITE_SENTRY_DSN is configured.
initSentry();

function TenantBrandingProvider({ children }: { children: React.ReactNode }) {
  useTenantBranding();
  return <>{children}</>;
}

// The prefix keeps Styletron's sequential atomic class names (a, b, ..., fc, ...)
// from colliding with short handwritten classes in global.css (.fc, .btn, .err, ...).
// Without it, once the app generates enough rules the atomic class "fc" lands on
// arbitrary elements and `.fc input` restyles BaseUI inputs/selects (SAM-136).
const engine = new Styletron({ prefix: 'st-' });
const queryClient = new QueryClient();

// Create a custom theme with system fonts
const customTheme = createTheme({
  ...LightTheme,
  typography: {
    ...LightTheme.typography,
    fontFamily: 'var(--font-primary)',
  },
});

// Suppress known BaseUI warnings in development
if (process.env.NODE_ENV === 'development') {
  const originalError = console.error;
  const originalWarn = console.warn;
  
  console.error = (...args: any[]) => {
    if (
      typeof args[0] === 'string' &&
      (args[0].includes('findDOMNode is deprecated') ||
       args[0].includes('defaultProps will be removed'))
    ) {
      return;
    }
    originalError.call(console, ...args);
  };
  
  console.warn = (...args: any[]) => {
    if (
      typeof args[0] === 'string' &&
      (args[0].includes('findDOMNode is deprecated') ||
       args[0].includes('defaultProps will be removed'))
    ) {
      return;
    }
    originalWarn.call(console, ...args);
  };
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <StyletronProvider value={engine}>
      <BaseProvider theme={customTheme}>
        <QueryClientProvider client={queryClient}>
          <ToasterContainer
            overrides={{
              Root: {
                style: {
                  zIndex: 30000,
                  bottom: '24px',
                  right: '24px',
                },
              },
              ToastBody: {
                style: ({ $kind }: any) => {
                  const borderColor =
                    $kind === 'positive' ? '#10B981' :
                    $kind === 'negative' ? '#EF4444' :
                    $kind === 'warning' ? '#F59E0B' :
                    'var(--tenant-primary, #111827)';
                  return {
                    backgroundColor: '#fff',
                    color: '#111827',
                    borderRadius: '10px',
                    borderLeft: `4px solid ${borderColor}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
                    padding: '12px 16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    minWidth: '260px',
                    maxWidth: '380px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  };
                },
              },
              ToastCloseIcon: {
                style: {
                  color: '#9CA3AF',
                  width: '14px',
                  height: '14px',
                  flexShrink: '0',
                  cursor: 'pointer',
                },
              },
            }}
          >
            <BrowserRouter>
              {/* SAM-683: admin language (EN/ES). The public pages keep their own (PublicLangProvider). */}
              <AdminLangProvider>
                <TenantBrandingProvider>
                  <BusinessProvider>
                    {/* SAM-226: catch render errors, show a fallback, and report to Sentry */}
                    <ErrorBoundary>
                      <LayoutPage />
                    </ErrorBoundary>
                  </BusinessProvider>
                </TenantBrandingProvider>
              </AdminLangProvider>
            </BrowserRouter>
          </ToasterContainer>
        </QueryClientProvider>
      </BaseProvider>
    </StyletronProvider>
  </React.StrictMode>
);
